import './Chat.styles.css';

import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useEventListener } from 'primereact/hooks';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { OverlayPanel } from 'primereact/overlaypanel';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useStompClient } from 'react-stomp-hooks';

import { ANTI_SPAM_DELAY } from '../../constants';
import { useOverlayClickOutside } from '../../hooks/useOverlayClickOutside';
import { useWebsocketSubscription } from '../../hooks/useWebsocketSubscription';
import { Permission } from '../../model/enumeration/Permission.enum';
import { Player } from '../../model/Player';
import authStore from '../../store/AuthStore';
import chatStore from '../../store/ChatStore';
import localStorageStore from '../../store/LocalStorageStore';
import permissionStore from '../../store/PermissionStore';
import playerStore from '../../store/PlayerStore';
import { WEventsDispatcher } from '../../websocket/handlers/WEventsDispatcher';
import { MessageBox } from '../shared/MessageBox/MessageBox';
import {
  getEmojiImage,
  getEmojiValues,
  sendMessageToChat,
} from './chat.render.utils';
import { ChatProps } from './Chat.types';

const Chat: FC<ChatProps> = ({ table, toggleEvent }) => {
  const overlayRef = useRef<OverlayPanel>(null);

  const [botPlayers, setBotPlayers] = useState<Player[]>([]);
  const [selectedBot, setSelectedBot] = useState<Player>();
  const [isChatOpen, setChatOpen] = useState(false);

  const [chatHeight, setChatHeight] = useState(
    localStorageStore.getChatHeight(),
  );
  const [isChatHeightSynced, setIsChatHeightSynced] = useState(false);

  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const [text, setText] = useState('');

  const client = useStompClient();

  const chatMessages = useMemo(
    () => (isChatOpen ? chatStore.getChatMessages(table.id) : []),
    [chatStore.chatStateChanged, isChatOpen],
  );

  useOverlayClickOutside(overlayRef);

  useWebsocketSubscription(
    `/user/tables/${table.id}/chat-messages`,
    WEventsDispatcher.processEventMessage,
    table.id,
  );

  const hasChatPermission = useMemo(
    () => permissionStore.hasPermission(Permission.CHAT),
    [authStore.authenticatedUser],
  );

  const messagesElementRef = useRef<HTMLDivElement>(null);

  const scrollChatEnd = (): void => {
    if (chatStore.isScrollChatEnd(table.id)) {
      const messagesElement = messagesElementRef.current;
      if (messagesElement) {
        messagesElement.scrollTop = messagesElement.scrollHeight;
      }
    }
  };

  const sendMessage = (textMessage: string, emoji: boolean): void => {
    if (!hasChatPermission) {
      return;
    }
    sendMessageToChat(
      textMessage,
      emoji,
      chatMessages,
      selectedBot,
      table,
      client,
      setShowErrorMessage,
      setText,
    );
  };

  const [bindKeyDown, unbindKeyDown] = useEventListener({
    type: 'keydown',
    listener: (e: KeyboardEvent) => {
      if (e.code === 'Enter') {
        sendMessage(text, false);
      }
    },
  });

  const updateSelectedBot = (bot: Player): void => {
    setSelectedBot(bot);
    chatStore.addSelectedBot(table.id, bot);
  };

  useEffect(() => {
    bindKeyDown();
    return () => {
      unbindKeyDown();
    };
  }, [bindKeyDown, unbindKeyDown]);

  useEffect(() => {
    if (!isChatOpen) {
      return;
    }
    const bots = playerStore.findBots(table.players);
    setBotPlayers(bots);
    const currentBot = chatStore.getSelectedBot(table.id);
    if (currentBot) {
      updateSelectedBot(bots.find(bot => bot.pid === currentBot.pid));
    } else if (bots.length === 1) {
      updateSelectedBot(bots[0]);
    }
  }, [table.players, isChatOpen]);

  useEffect(() => {
    if (overlayRef.current && toggleEvent) {
      overlayRef.current.toggle(toggleEvent);
    } else if (overlayRef.current) {
      overlayRef.current.hide();
    }
  }, [toggleEvent]);

  useEffect(() => {
    if (isChatOpen) {
      const bot = chatStore.getSelectedBot(table.id);
      setSelectedBot(bot);
      scrollChatEnd();
      chatStore.markMessagesAsRead(table.id);
    }
  }, [isChatOpen, chatMessages.length]);

  useEffect(() => {
    if (showErrorMessage) {
      scrollChatEnd();
    }
  }, [showErrorMessage]);

  useEffect(() => {
    if (!isChatOpen) {
      setIsChatHeightSynced(false);
      return;
    }
    if (chatHeight !== localStorageStore.getChatHeight()) {
      setChatHeight(localStorageStore.getChatHeight);
    } else {
      setIsChatHeightSynced(true);
    }
  }, [isChatOpen, chatHeight]);

  useEffect(() => {
    if (isChatOpen && isChatHeightSynced) {
      const overlayPanelContent = overlayRef.current
        ?.getElement()
        ?.querySelector('.p-overlaypanel-content');

      if (!overlayPanelContent) return;

      const resizeObserver = new ResizeObserver(entries => {
        const updatedHeight = entries[0].borderBoxSize[0].blockSize;
        setChatHeight(updatedHeight);
        localStorageStore.setChatHeight(updatedHeight);
      });

      resizeObserver.observe(overlayPanelContent);

      return () => resizeObserver.disconnect();
    }
  }, [isChatOpen, isChatHeightSynced]);

  const emojiValues: string[] = useMemo(() => getEmojiValues(), []);

  const emojiContent = emojiValues?.map(emojiValue => (
    <span
      className="cursor-pointer m-2"
      key={emojiValue}
      onClick={() => sendMessage(emojiValue, true)}
    >
      {getEmojiImage(emojiValue)}
    </span>
  ));

  const onHide = (): void => {
    chatStore.removeToggleEvent(table.id);
    setChatOpen(false);
  };

  return (
    <OverlayPanel
      id={`chat-${table.id}`}
      onShow={() => setChatOpen(true)}
      onHide={onHide}
      className="mt-2 chat-overlay"
      ref={overlayRef}
      pt={{
        content: {
          style: {
            height: chatHeight,
            padding: emojiContent ? '0 0 30px' : '0',
          },
        },
      }}
      showCloseIcon
      dismissable={false}
      onClick={e => e.stopPropagation()}
      data-ignore-outside-click
    >
      <div className="header p-2">
        <h3 className="m-0">Chat</h3>
      </div>
      <div className="chat-content p-2">
        <div ref={messagesElementRef} className="messages-wrapper p-3">
          {chatMessages.map(message => (
            <MessageBox
              messageId={message.id}
              key={message.id}
              style={{
                background: message.myBot
                  ? '#074c2f'
                  : 'var(--clubgg-card-background)',
              }}
              message={message}
            />
          ))}
          {showErrorMessage && (
            <Message
              severity="error"
              text={`Min delay between duplicated messages is ${
                ANTI_SPAM_DELAY / 1000
              } seconds`}
            />
          )}
        </div>
        <div>
          <div className="row justify-content-center input-wrapper mb-3">
            <Dropdown
              className="bot-selector p-0 me-1"
              value={selectedBot}
              onChange={e => updateSelectedBot(e.value)}
              invalid={!selectedBot}
              disabled={!hasChatPermission}
              options={botPlayers}
              optionLabel="nick"
              placeholder="Select bot"
            />
            <InputText
              className="col-5 me-1"
              value={text}
              disabled={!hasChatPermission}
              maxLength={200}
              onChange={e => setText(e.target.value)}
            />
            <Button
              className="col-1"
              icon="pi pi-send"
              rounded
              text
              disabled={!text || !hasChatPermission || !selectedBot}
              severity="success"
              aria-label="send"
              onClick={() => sendMessage(text, false)}
            />
          </div>
          <div className="emoji-wrapper px-3">{emojiContent}</div>
        </div>
      </div>
    </OverlayPanel>
  );
};

export default observer(Chat);
